import { Box, Typography, Grid, TextField } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { Assignment, Person, Edit, Save } from '@material-ui/icons';
import { unpackToDate, encodeDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { DatePickerField } from '@campfire/date-picker/lib';
import { TabletButton } from '@campfire/tablet-button';
import { Formik, Form, Field, FieldProps, FormikHelpers } from 'formik';
import { object as YupObject, string as YupString, date as YupDate } from 'yup';
import { phoneNumberValidator } from '../../../../../common/form/task-form/model/validation-schema';
import PrivilegeRules from '../../../../../global/auth/PrivilegeRules';
import { useUser } from '../../../../../global/auth/useUser';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import {
  VolunteerCommonProfile,
  VolunteerCommonProfile_profile_completedProfileTask as CompletedProfileTask,
} from '../../__generated__/VolunteerCommonProfile';
import { InfoBlock } from '../../common/InfoBlock';
import { hasRuleForVolunteer } from '../../common/utils';
import { VolunteerComments } from './volunteer-comments/VolunteerComments';
import { VolunteerRoles } from './VolunteerRoles';
import { CompletedTaskViewer } from '../../../../../common/form/task-form/CompletedTaskViewer';
import { deserializeTaskItems } from '../../../../admin/tasks/utils';
import { GetVolunteerCommonProfile_vm_profileTask as ProfileTask } from '../../__generated__/GetVolunteerCommonProfile';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';

// Validation schema for profile editing
const editProfileValidationSchema = YupObject().shape({
  firstName: YupString().required('First name cannot be empty'),
  lastName: YupString().required('Last name cannot be empty'),
  preferredName: YupString(),
  dateOfBirth: YupDate()
    .required()
    .max('2100-01-01', 'Date is after max limit')
    .min('1900-01-01', 'Date is before min limit'),
  email: YupString()
    .required('An email is required')
    .email('Please enter a valid email'),
  contactNumber: phoneNumberValidator(false),
});

interface EditProfileFormValues {
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: Date | null;
  email: string;
  contactNumber: string;
}

interface EditProfileSpec {
  profileId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  email: string;
  phoneNumber: string;
  aboutMe: string;
}

export const VolunteerPersonalDetail = ({
  volunteer,
  onClose,
  refetch,
  profileTask,
}: {
  volunteer: VolunteerCommonProfile;
  onClose: () => void;
  refetch: () => void;
  profileTask?: ProfileTask;
}) => {
  const {
    volunteerId,
    flagging,
    dateCreated,
    application,
    profile: {
      profileId,
      firstName,
      lastName,
      preferredName,
      email,
      contactNumber,
      dateOfBirth,
      description,
      completedProfileTask,
    },
    activityRoles,
  } = volunteer;
  const {
    user: { userIdentityService },
  } = useUser();
  const { lightTextColor } = getAlertCardColors('urgent');
  const { lightTextColor: infoTextColor } = getAlertCardColors('info');
  const { setSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);

  const canUpdateActivityRoles =
    volunteer && hasRuleForVolunteer(volunteer, userIdentityService, PrivilegeRules.updateVolunteerActivityRoles);
  const canUpdateComments =
    volunteer && hasRuleForVolunteer(volunteer, userIdentityService, PrivilegeRules.updateVolunteerComments);
  const canEditProfile = userIdentityService.isProgramManager || userIdentityService.isVmAdmin;

  const updateProfileFetch = useEndpointFetch<EditProfileSpec>('/vm/volunteer/profile/edit');

  useMemo(() => {
    refetch();
  }, []);

  const handleEditProfile = (
    values: EditProfileFormValues,
    { setFieldError }: FormikHelpers<EditProfileFormValues>
  ) => {
    setFieldError('email', undefined);
    const parcel: EditProfileSpec = {
      profileId,
      firstName: values.firstName,
      lastName: values.lastName,
      preferredName: values.preferredName === '' ? values.firstName : values.preferredName,
      dateOfBirth: values.dateOfBirth ? encodeDate(DateTime.fromJSDate(values.dateOfBirth)) : '',
      email: values.email,
      phoneNumber: values.contactNumber,
      aboutMe: description || '', // Keep existing description
    };

    updateProfileFetch
      .run(parcel)
      .then((response) => {
        if (response && response.ok) {
          setSnackbar({
            message: 'Profile updated successfully',
            variant: 'success',
            open: true,
          });
          setIsEditing(false);
          refetch();
          return;
        }

        if (response.status === 409) {
          setFieldError('email', 'This email is already in use');
          setSnackbar({
            message: 'Unable to update profile',
            variant: 'error',
            open: true,
          });
          return;
        }

        setSnackbar({
          message: 'Unable to update profile',
          variant: 'error',
          open: true,
        });
      })
      .catch(() => {
        setSnackbar({
          message: 'Unable to update profile',
          variant: 'error',
          open: true,
        });
      });
  };

  const initialValues: EditProfileFormValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    preferredName: preferredName === firstName ? '' : preferredName || '',
    dateOfBirth: dateOfBirth ? unpackToDate(dateOfBirth).toJSDate() : null,
    email: email || '',
    contactNumber: contactNumber || '',
  };

  return (
    <>
      <Box p={2}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography
            style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
          >
            <Person style={{ fontSize: '14px', paddingRight: '5px' }} /> Personal Details
          </Typography>
          {canEditProfile && !isEditing && (
            <TabletButton
              variant='outlined'
              color='primary'
              size='small'
              onClick={() => setIsEditing(true)}
              disabled={updateProfileFetch.isLoading}
              startIcon={<Edit />}
            >
              Edit
            </TabletButton>
          )}
        </Box>

        {flagging && (
          <AlertCard variant='urgent' title='User Flagged & Exited'>
            <Typography display='block' component='span' style={{ color: lightTextColor, fontSize: '14px' }}>
              Exited by{' '}
              <strong>
                {flagging.flaggedBy.profile.preferredName} {flagging.flaggedBy.profile.lastName}
              </strong>
              <br />
              {`Reason: ${flagging.flaggedReason}`}
            </Typography>
          </AlertCard>
        )}

        {isEditing ? (
          <>
            <Box mb={3}>
              <AlertCard variant='info' title='Updating Details'>
                <Typography style={{ fontSize: '14px', fontWeight: 400, color: infoTextColor }}>
                  Changing the contact email will not affect their login email. The volunteer will be notified of any
                  profile updates.
                </Typography>
              </AlertCard>
            </Box>
            <Formik
              initialValues={initialValues}
              validationSchema={editProfileValidationSchema}
              onSubmit={handleEditProfile}
            >
              {({ isSubmitting }) => (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Field name='firstName'>
                        {({ field, meta }: FieldProps) => (
                          <TextField
                            {...field}
                            label='First Name'
                            variant='outlined'
                            fullWidth
                            required
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name='lastName'>
                        {({ field, meta }: FieldProps) => (
                          <TextField
                            {...field}
                            label='Last Name'
                            variant='outlined'
                            fullWidth
                            required
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name='preferredName'>
                        {({ field, meta }: FieldProps) => (
                          <TextField
                            {...field}
                            label='Preferred Name'
                            variant='outlined'
                            fullWidth
                            error={meta.touched && !!meta.error}
                            helperText={(meta.touched && meta.error) || 'Leave blank to use first name'}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePickerField
                        name='dateOfBirth'
                        label='Date of Birth'
                        autoOk
                        fullWidth
                        inputVariant='outlined'
                        InputLabelProps={{ shrink: true }}
                        required
                        maxDate={new Date()}
                        minDate={new Date('1900-01-01')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name='email'>
                        {({ field, meta }: FieldProps) => (
                          <TextField
                            {...field}
                            label='Email'
                            variant='outlined'
                            fullWidth
                            required
                            type='email'
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name='contactNumber'>
                        {({ field, meta }: FieldProps) => (
                          <TextField
                            {...field}
                            label='Phone Number'
                            variant='outlined'
                            fullWidth
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display='flex' justifyContent='flex-end' mt={4}>
                        <TabletButton
                          variant='text'
                          onClick={() => setIsEditing(false)}
                          disabled={isSubmitting || updateProfileFetch.isLoading}
                          style={{ marginRight: 16, paddingLeft: 8, paddingRight: 8 }}
                        >
                          Cancel
                        </TabletButton>
                        <TabletButton
                          type='submit'
                          variant='contained'
                          color='primary'
                          disabled={isSubmitting || updateProfileFetch.isLoading}
                          startIcon={<Save />}
                        >
                          Save Changes
                        </TabletButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <>
            <InfoBlock
              label='Name'
              value={`${firstName} ${lastName}${
                preferredName && preferredName !== firstName ? ` (${preferredName})` : ''
              }`}
            />
            <InfoBlock label='Email' value={email || 'Not provided'} />
            <InfoBlock label='Phone Number' value={contactNumber || 'Not provided'} />
            {dateOfBirth && <InfoBlock label='Date of Birth' value={dateOfBirth} />}
            {dateCreated && (
              <InfoBlock label='Profile Approval Date' value={unpackToDate(dateCreated).toFormat('d LLLL, yyyy')} />
            )}
            {application && (
              <InfoBlock
                label='Application Date'
                value={unpackToDate(application.dateSubmitted).toFormat('d LLLL, yyyy')}
              />
            )}
          </>
        )}

        <Box mt={3}>
          <VolunteerComments
            volunteerId={volunteerId}
            comments={description}
            editable={canUpdateComments}
            onClose={onClose}
            refetch={refetch}
          />
        </Box>
        <VolunteerRoles
          volunteerId={volunteerId}
          volunteerActivityRoles={activityRoles}
          editable={canUpdateActivityRoles}
        />
      </Box>
      {completedProfileTask && profileTask && (
        <>
          <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />
          <TaskItems profileTask={profileTask} completedProfileTask={completedProfileTask} />
        </>
      )}
    </>
  );
};

interface TaskItemProps {
  profileTask: ProfileTask;
  completedProfileTask: CompletedProfileTask;
}

const TaskItems = (props: TaskItemProps) => {
  const { profileTask, completedProfileTask } = props;

  const taskHeaderDetails = useMemo(() => {
    return {
      title: completedProfileTask.task.title,
      description: completedProfileTask.task.description,
      cake: completedProfileTask.task.cake.title,
    };
  }, [completedProfileTask]);

  const taskItems = useMemo(() => {
    if (!profileTask) return [];
    return profileTask.taskItems.sort((a, b) => (a.order > b.order ? 1 : -1));
  }, [completedProfileTask]);

  const selectedCompletedTask = completedProfileTask;

  const taskItemValues = useMemo(() => {
    if (!selectedCompletedTask) return [];
    return selectedCompletedTask.taskFieldValues.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.field.taskFieldId]: curr,
      }),
      {}
    );
  }, [selectedCompletedTask]);

  const parsedTaskItems = deserializeTaskItems(taskItems);

  return (
    <Box p={2}>
      <Typography style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}>
        <Assignment style={{ fontSize: '14px', paddingRight: '5px' }} /> Profile Responses
      </Typography>
      <CompletedTaskViewer
        taskItems={parsedTaskItems}
        taskItemValues={taskItemValues}
        volunteerProfileValues={undefined}
        taskHeaderDetails={taskHeaderDetails}
        selectedCompletedTask={selectedCompletedTask}
        isTaskZero={false}
        simple
      />
    </Box>
  );
};
